import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import api from "../utils/api";

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

export default function ItemMasterEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemType, setItemType] = useState("");
  const [unitName, setUnitName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [units, setUnits] = useState([]);

  useEffect(() => {
    async function loadData() {
      try {
        const [itemRes, unitRes] = await Promise.all([
          api.get(`/api/item-master/${id}`),
          api.get("/api/unit-master"),
        ]);

        const r = itemRes.data || {};

        setItemCode(r.item_code || "");
        setItemName(r.item_name || "");
        setItemType(r.item_type || "");
        setUnitName(r.unit_name || "");
        setIsActive(Number(r.is_active) === 1);

        setUnits(toArray(unitRes.data));
      } catch (err) {
        alert(err?.response?.data?.error || err.message || "Load failed");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [id]);

  const unitOptions = useMemo(() => {
    return units
      .map((u) => {
        const code = String(u?.sCode || "").trim();
        const name = String(u?.sName || "").trim();

        if (!code) return null;

        return {
          value: code, // ✅ store code like Nos / Mtr
          label: name ? `${code} - ${name}` : code,
          searchText: `${code} ${name}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [units]);

  const selectedUnit =
    unitOptions.find(
      (opt) => String(opt.value).toLowerCase() === String(unitName).toLowerCase()
    ) || null;

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      height: "38px",
      border: "1px solid #ced4da",
      boxShadow: "none",
      borderRadius: "0.375rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "38px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "38px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "6px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "6px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "6px",
      marginBottom: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  async function save() {
    if (!itemCode.trim()) return alert("Item Code required");
    if (!itemName.trim()) return alert("Item Name required");
    if (!itemType.trim()) return alert("Item Type required");
    if (!unitName.trim()) return alert("Unit Name required");

    try {
      setSaving(true);

      await api.put(`/api/item-master/${id}`, {
        item_code: itemCode.trim(),
        item_name: itemName.trim(),
        item_type: itemType.trim(),
        unit_name: unitName.trim(), // ✅ saves code
        is_active: isActive,
      });

      alert("Updated successfully");
      navigate("/item-master");
    } catch (err) {
      alert(err?.response?.data?.error || err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-3">Loading...</div>;

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Edit Item Master</h2>

        <div className="d-flex gap-2">
          <button className="btn btn-dark" onClick={save} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>

          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/item-master")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body row g-3">
          <div className="col-md-4">
            <label className="form-label">Item Code</label>
            <input
              className="form-control"
              value={itemCode}
              onChange={(e) => setItemCode(e.target.value)}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Item Name</label>
            <input
              className="form-control"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Item Type</label>
            <select
              className="form-select"
              value={itemType}
              onChange={(e) => setItemType(e.target.value)}
            >
              <option value="">-- Select --</option>
              <option value="extruder">extruder</option>
              <option value="injection">injection</option>
              <option value="recycle">recycle</option>
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Unit Name</label>
            <Select
              options={unitOptions}
              value={selectedUnit}
              onChange={(selected) =>
                setUnitName(selected ? selected.value : "")
              }
              placeholder="-- Select Unit --"
              isClearable
              isSearchable
              styles={customSelectStyles}
              noOptionsMessage={() => "No unit found"}
              filterOption={(option, inputValue) => {
                const text =
                  `${option.label} ${option.data.searchText || ""}`.toLowerCase();
                return text.includes(String(inputValue).toLowerCase());
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Active</label>
            <select
              className="form-select"
              value={isActive ? "1" : "0"}
              onChange={(e) => setIsActive(e.target.value === "1")}
            >
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}